import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import { Achievement } from "@/hooks/useAchievements";
import { useNotificationSettings } from "@/hooks/useNotificationSettings";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { DEFAULT_NOTIFICATION_SOUND } from "@/constants/sounds";

interface AchievementNotificationContextType {
  showNotification: (achievement: Achievement) => void;
  hideNotification: () => void;
  skipAll: () => void;
  currentAchievement: Achievement | null;
  isOpen: boolean;
  queueLength: number;
}

const AchievementNotificationContext = createContext<AchievementNotificationContextType | null>(null);

export function AchievementNotificationProvider({ children }: { children: React.ReactNode }) {
  const [queue, setQueue] = useState<Achievement[]>([]);
  const [currentAchievement, setCurrentAchievement] = useState<Achievement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  
  const { config } = useNotificationSettings();
  const { soundEnabled, animationsEnabled } = useUserPreferences();

  // Unlock audio on first user interaction
  useEffect(() => {
    const handleUnlock = async () => {
      const { unlockAudio } = await import("@/utils/sound");
      await unlockAudio();
      document.removeEventListener("pointerdown", handleUnlock);
    };
    
    document.addEventListener("pointerdown", handleUnlock, { once: true });
    return () => document.removeEventListener("pointerdown", handleUnlock);
  }, []);

  // Pre-load audio with fallback — reuses or replaces the audio instance
  // rather than creating new Audio() objects that are never destroyed.
  useEffect(() => {
    const soundUrl = config.soundUrl || DEFAULT_NOTIFICATION_SOUND;

    const audio = new Audio(soundUrl);
    audio.volume = config.soundVolume;

    const handleCanPlay = () => {
      audioRef.current = audio;
    };

    const handleError = () => {
      if (config.soundUrl) {
        const fallbackAudio = new Audio(DEFAULT_NOTIFICATION_SOUND);
        fallbackAudio.volume = config.soundVolume;

        const onFallbackReady = () => { audioRef.current = fallbackAudio; };
        const onFallbackError = () => { audioRef.current = null; };

        fallbackAudio.addEventListener("canplaythrough", onFallbackReady, { once: true });
        fallbackAudio.addEventListener("error", onFallbackError, { once: true });
        fallbackAudio.load();
      } else {
        audioRef.current = null;
      }
    };

    audio.addEventListener("canplaythrough", handleCanPlay, { once: true });
    audio.addEventListener("error", handleError, { once: true });
    audio.load();

    return () => {
      audio.removeEventListener("canplaythrough", handleCanPlay);
      audio.removeEventListener("error", handleError);
      // Stop playback and release browser resources for this instance
      audio.pause();
      audio.src = "";
      if (audioRef.current === audio) {
        audioRef.current = null;
      }
    };
  }, [config.soundVolume, config.soundUrl]);

  const playSound = useCallback(async () => {
    if (!soundEnabled) return;
    
    // Try HTMLAudioElement first
    if (audioRef.current) {
      audioRef.current.volume = config.soundVolume;
      audioRef.current.currentTime = 0;
      try {
        await audioRef.current.play();
        return;
      } catch {
        // Fall through to Web Audio fallback
      }
    }
    
    // Web Audio API fallback
    const { playBeepFallback } = await import("@/utils/sound");
    await playBeepFallback(config.soundVolume);
  }, [soundEnabled, config.soundVolume]);

  const showNext = useCallback(() => {
    if (queue.length > 0) {
      const [next, ...rest] = queue;
      setCurrentAchievement(next);
      setQueue(rest);
      setIsOpen(true);
      playSound();
    } else {
      setCurrentAchievement(null);
      setIsOpen(false);
    }
  }, [queue, playSound]);

  const showNotification = useCallback((achievement: Achievement) => {
    if (!animationsEnabled) {
      // If animations disabled, don't show the modal notification
      // The achievement is still unlocked, just no fancy UI
      return;
    }

    if (isOpen) {
      // Add to queue if already showing one
      setQueue((prev) => [...prev, achievement]);
    } else {
      setCurrentAchievement(achievement);
      setIsOpen(true);
      playSound();
    }
  }, [isOpen, animationsEnabled, playSound]);

  const hideNotification = useCallback(() => {
    setIsOpen(false);
    // Show next in queue after a small delay
    setTimeout(() => {
      showNext();
    }, 300);
  }, [showNext]);

  const skipAll = useCallback(() => {
    setQueue([]);
    setCurrentAchievement(null);
    setIsOpen(false);
  }, []);

  // Auto-close if enabled
  useEffect(() => {
    if (isOpen && config.autoCloseEnabled && currentAchievement) {
      const timer = setTimeout(() => {
        hideNotification();
      }, config.autoCloseDelayMs);
      return () => clearTimeout(timer);
    }
  }, [isOpen, config.autoCloseEnabled, config.autoCloseDelayMs, currentAchievement, hideNotification]);

  return (
    <AchievementNotificationContext.Provider
      value={{
        showNotification,
        hideNotification,
        skipAll,
        currentAchievement,
        isOpen,
        queueLength: queue.length,
      }}
    >
      {children}
    </AchievementNotificationContext.Provider>
  );
}

export function useAchievementNotification() {
  const context = useContext(AchievementNotificationContext);
  if (!context) {
    throw new Error("useAchievementNotification must be used within AchievementNotificationProvider");
  }
  return context;
}
